'use client'

import { useEffect } from 'react'
import { localeDirections } from '@/i18n/config'

/**
 * Client component that sets <html> lang and dir attributes.
 * Also prevents layout shift when Radix scroll-locks body.
 *
 * The problem: react-remove-scroll-bar adds overflow:hidden + margin-right
 * to body when dropdowns/modals open. This hides the scrollbar and shifts
 * the page. CSS scrollbar-gutter:stable doesn't work reliably in Safari.
 *
 * Fix: Force overflow-y:scroll on html (scrollbar always visible) and
 * observe data-scroll-locked on body to zero out compensation via
 * inline !important styles (unbeatable by any CSS rule).
 */
export function HtmlLangSetter({ locale }: { locale: string }) {
  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = (localeDirections as Record<string, string>)[locale] ?? 'ltr'
  }, [locale])

  // Prevent scroll-lock layout shift
  useEffect(() => {
    const body = document.body

    const resetScrollLockStyles = () => {
      if (body.hasAttribute('data-scroll-locked')) {
        body.style.setProperty('margin-right', '0', 'important')
        body.style.setProperty('margin-left', '0', 'important')
        body.style.setProperty('padding-right', '0', 'important')
        body.style.setProperty('padding-left', '0', 'important')
      } else {
        body.style.removeProperty('margin-right')
        body.style.removeProperty('margin-left')
        body.style.removeProperty('padding-right')
        body.style.removeProperty('padding-left')
      }
    }

    // Keep viewport scrollbar always visible so it never disappears on scroll lock.
    // react-remove-scroll only sets overflow:hidden on body, not html.
    document.documentElement.style.setProperty('overflow-y', 'scroll')

    const observer = new MutationObserver(resetScrollLockStyles)
    observer.observe(body, { attributes: true, attributeFilter: ['data-scroll-locked'] })

    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('overflow-y')
      body.style.removeProperty('margin-right')
      body.style.removeProperty('margin-left')
      body.style.removeProperty('padding-right')
      body.style.removeProperty('padding-left')
    }
  }, [locale])

  return null
}
